"use strict";
// Where a project's urban study stands, from the commercial's point of view.
//
// Two facts decide it and both matter: WHO ordered the study, and WHETHER it has
// been delivered. Splitting them across surfaces is how issue #631 happened —
// the salesman hub's banner said "Étude urbaine en cours" while the send button
// beside it stayed open, so a dossier could be transmitted to the architect
// while the study it is meant to rest on was still being instructed.
//
// Single source of truth for the three-way answer. The hub banner
// (`useUrbanStudyNotice`), the send gate (`useProjectHubSendGate`) and the
// server-side `request` guard all read it, so the button, the copy and the
// action cannot disagree.
Object.defineProperty(exports, "__esModule", { value: true });
exports.canCancelUrbanStudy = exports.isUrbanStudyPendingDelivery = exports.urbanStudyStage = exports.isUrbanStudyCommercialSide = void 0;
const urban_analysis_requestor_1 = require("../accounts/urban-analysis-requestor");
const status_1 = require("./status");
// A study belongs to the commercial side only when the commercial or the client
// ordered it. SOCIETY never appears here: a company orders on its salesman's
// behalf and is recorded as SALESMAN (see `urbanAnalysisRequestorForRole`).
const COMMERCIAL_SIDE_REQUESTORS = new Set([
    urban_analysis_requestor_1.URBAN_ANALYSIS_REQUESTOR.SALESMAN,
    urban_analysis_requestor_1.URBAN_ANALYSIS_REQUESTOR.CLIENT,
]);
/**
 * True when the study was ordered by the commercial side. `null`/`undefined`
 * (no requestor recorded) and the architect/collaborator requestors return
 * false — the commercial views must still treat those as "no study of mine".
 */
const isUrbanStudyCommercialSide = (requestor) => requestor != null && COMMERCIAL_SIDE_REQUESTORS.has(requestor);
exports.isUrbanStudyCommercialSide = isUrbanStudyCommercialSide;
const urbanStudyStage = (study) => {
    if (!study || !(0, exports.isUrbanStudyCommercialSide)(study.requestor))
        return 'none';
    // Cancelled BEFORE the pending fallback: a called-off study is not "still
    // being instructed". Reading it as `pending` would leave the hub banner
    // announcing a study nobody is writing and the send gate shut for good.
    if ((0, status_1.isFormCancelled)(study.status))
        return 'none';
    return study.status === status_1.FormStatus.VALIDATED ? 'delivered' : 'pending';
};
exports.urbanStudyStage = urbanStudyStage;
/**
 * True while the commercial's own study is still being instructed — ordered but
 * not yet VALIDATED (EMPTY, EDITED, COMPLETED and REFUSED all count).
 *
 * COMPLETED is deliberately NOT delivered: the architect has drafted the study
 * but not signed it off, no PDF exists and the commercial is never notified.
 * VALIDATED is the same threshold the architect's own acceptance gate uses
 * (#485), so "delivered" means one thing across the product.
 */
const isUrbanStudyPendingDelivery = (study) => (0, exports.urbanStudyStage)(study) === 'pending';
exports.isUrbanStudyPendingDelivery = isUrbanStudyPendingDelivery;
/**
 * May this study still be called off? (#695)
 *
 * Everything short of acceptance can: a study is cancellable while it is still
 * being instructed (EMPTY, EDITED, COMPLETED) and after it was sent back
 * (REFUSED, which is "awaiting modification", not an ending). Two states say
 * no, and for opposite reasons — VALIDATED is delivered work, already filed and
 * mailed to whoever ordered it, and CANCELLED is already the answer.
 *
 * Written as the list of states that ALLOW it rather than the two that forbid
 * it, so anything else answers no: an absent status (a study whose row has not
 * loaded — offering the action then would put "Annuler l'étude" on a delivered
 * study for as long as the fetch takes), a value this build does not know, and
 * any status added to the enum later. Absence is not permission, and neither is
 * unfamiliarity.
 *
 * The rule is stated once here because three places ask it: the architect's
 * editor header, the commercial's study page, and the server, which is the only
 * one of the three that decides anything.
 */
const CANCELLABLE_STATUSES = new Set([
    status_1.FormStatus.EMPTY,
    status_1.FormStatus.EDITED,
    status_1.FormStatus.COMPLETED,
    status_1.FormStatus.REFUSED,
]);
const canCancelUrbanStudy = (status) => status != null && CANCELLABLE_STATUSES.has(status);
exports.canCancelUrbanStudy = canCancelUrbanStudy;
//# sourceMappingURL=urban-study.js.map